/**
 * L'EFFIGIE D'UNE CRÉATURE — ce qu'on affronte a une tête, toujours (design §3).
 *
 * POURQUOI CE FICHIER EXISTE. Les illustrations du §6 se dessinent à la main et se posent une par
 * une : la plupart des aventures n'en auront aucune pendant des mois. Pour un bandeau de nœud, la
 * règle « rien ne s'affiche » est la bonne — le texte décrit le lieu. Pour un adversaire, elle est
 * fausse : on frappe un nom pendant six tours, et un nom ne fait pas peur. L'interface dessine donc
 * la bête, et l'illustration posée la remplacera le jour où elle existera.
 *
 * CE QUI EST PUR EST ICI, LE TRAIT EST À CÔTÉ. Choisir une famille de silhouette et tirer les
 * variantes d'une créature, c'est une décision : elle se teste sur des chaînes, sans navigateur.
 * `composants/Effigie.tsx` ne fait que tracer ce que ce fichier a décidé.
 *
 * ON NE DÉDUIT QUE DE CE QUE LE JOUEUR VOIT DÉJÀ — le nom, l'arme, l'état qu'elle inflige, sa masse
 * de points de vie. Jamais d'une fiche cachée : le serveur ne renvoie pas les caractéristiques d'un
 * adversaire (c'est écrit dans la vue du combat), et une effigie qui trahirait sa défense ferait
 * entrer par le dessin ce qu'on a refusé de faire entrer par le JSON.
 */

/** Les six familles. Elles se reconnaissent AVANT le nom : c'est tout ce qu'on leur demande. */
export const FAMILLES = ["quadrupede", "humanoide", "essaim", "machine", "informe", "colosse"] as const;
export type Famille = (typeof FAMILLES)[number];

/** Ce que la bête porte au bout des membres. Le dessin s'en sert, et le joueur l'a déjà lu sur la
 * fiche de l'adversaire — c'est la même information, dite en trait. */
export const ARMEMENTS = ["griffes", "crocs", "dard", "lame", "jet", "masse"] as const;
export type Armement = (typeof ARMEMENTS)[number];

/**
 * L'EMPREINTE D'UNE CHAÎNE — le même mélange que le PRNG du moteur (mulberry32), replié sur 32 bits.
 *
 * Elle sert de graine à tout ce qui varie dans une effigie. Deux propriétés comptent, et ce sont
 * les seules : la MÊME créature donne toujours la MÊME tête (sans quoi on n'apprend jamais à
 * reconnaître une bête), et deux créatures voisines en donnent deux différentes.
 */
export function empreinte(chaine: string): number {
  let x = 0x9e3779b9;
  for (let i = 0; i < chaine.length; i++) {
    x = Math.imul(x ^ chaine.charCodeAt(i), 0x85ebca6b) >>> 0;
    x = (x ^ (x >>> 13)) >>> 0;
  }
  x = Math.imul(x ^ (x >>> 15), x | 1) >>> 0;
  return (x ^ (x >>> 14)) >>> 0;
}

/** Les mots qui nomment une famille, par famille. Ce sont ceux des lexiques de la génération
 * (`packages/generation/src/lexique.ts`) plus ceux du bestiaire du socle : une liste fermée, et un
 * repli sur l'arme quand aucun ne parle. */
const MOTS: Record<Famille, string[]> = {
  quadrupede: ["loup", "chien", "bete", "bête", "meute", "charognard", "araignee", "araignée", "rat", "sanglier", "cheval", "fauve"],
  humanoide: ["rodeur", "rôdeur", "goule", "homme", "silhouette", "servante", "passager", "chasseur", "brigand", "garde", "soldat", "pretre", "prêtre", "aieul", "aïeul", "groupe"],
  essaim: ["essaim", "colonie", "vermine", "nuee", "nuée", "insecte", "guepe", "guêpe", "corbeaux"],
  machine: ["automate", "drone", "unite", "unité", "sentinelle", "mecanisme", "mécanisme", "tourelle", "engin"],
  informe: ["chose", "ombre", "choeur", "chœur", "brume", "spectre", "fantome", "fantôme", "vase", "gelee", "gelée", "presence", "présence"],
  colosse: ["colosse", "titan", "geant", "géant", "montagne", "golem", "troll", "ogre"]
};

/** L'arme, telle que la vue du combat la nomme, vers ce qu'il faut dessiner. */
const PAR_ARME: { mots: string[]; armement: Armement; famille: Famille }[] = [
  { mots: ["griffe"], armement: "griffes", famille: "quadrupede" },
  { mots: ["morsure", "croc", "gueule", "mâchoire", "machoire"], armement: "crocs", famille: "quadrupede" },
  { mots: ["dard", "aiguillon", "piqure", "piqûre"], armement: "dard", famille: "essaim" },
  { mots: ["lame", "epee", "épée", "couteau", "hache", "pique", "coutelas"], armement: "lame", famille: "humanoide" },
  { mots: ["fronde", "arc", "jet", "fusil", "crachat"], armement: "jet", famille: "humanoide" },
  { mots: ["masse", "massue", "marteau", "poing", "charge"], armement: "masse", famille: "colosse" }
];

/** Sans accent et sans casse : « Rôdeur famélique » et « rodeur » sont le même mot pour qui cherche
 * une famille. On ne dépend d'aucune bibliothèque pour ça — `normalize` est dans la plateforme. */
function nu(texte: string): string {
  return texte
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export interface Signalement {
  /** L'identifiant de l'espèce, quand le serveur le donne : c'est la graine la plus stable. */
  espece?: string | null;
  nom: string;
  arme?: string | null;
  /** L'état qu'un coup porté peut infliger. */
  inflige?: string | null;
  pvMax?: number;
}

export interface Effigie {
  famille: Famille;
  armement: Armement;
  /** La graine des variantes : nombre d'yeux, inclinaison, cornes. Stable pour une espèce. */
  graine: number;
  /** De 0 à 1 : la place que la bête prend dans son cadre. Une bête qui a trois fois plus de points
   * de vie qu'une autre se voit de loin — c'est la seule chose que l'effigie dit des chiffres, et
   * elle est déjà écrite en toutes lettres sous le nom. */
  ampleur: number;
  /** Ce que le coup laisse derrière lui, quand il laisse quelque chose. Il n'ajoute qu'un attribut
   * au dessin (un suintement, une braise) : jamais une couleur porteuse d'information seule (§5). */
  stigmate: string | null;
}

/** Cent points de vie et au-delà, la bête remplit son cadre ; en dessous de dix, elle s'y perd. */
const PV_DE_REFERENCE = 60;

/**
 * CE QU'IL FAUT DESSINER, à partir de ce qui est affiché. L'ordre compte : le NOM d'abord, parce
 * que c'est ce que le joueur lit — une effigie qui dessine un loup là où il est écrit « Automate »
 * est une effigie qui ment —, l'ARME ensuite, et le hasard seedé en dernier recours.
 */
export function effigieDe(signalement: Signalement): Effigie {
  const nom = nu(signalement.nom ?? "");
  const arme = nu(signalement.arme ?? "");
  const graine = empreinte(signalement.espece || signalement.nom || "creature");

  let famille: Famille | null = null;
  for (const candidate of FAMILLES) {
    if (MOTS[candidate].some((mot) => nom.includes(nu(mot)))) {
      famille = candidate;
      break;
    }
  }

  const parArme = PAR_ARME.find((entree) => entree.mots.some((mot) => arme.includes(nu(mot))));
  const armement: Armement = parArme?.armement ?? (["griffes", "crocs", "lame"] as const)[graine % 3]!;
  if (!famille) famille = parArme?.famille ?? FAMILLES[graine % FAMILLES.length]!;

  // La masse passe avant tout le reste : ce qui a deux cents points de vie n'est pas un rôdeur,
  // quel que soit le mot qui le nomme.
  const pv = signalement.pvMax ?? 0;
  if (pv >= PV_DE_REFERENCE * 2.5 && famille !== "essaim" && famille !== "informe") famille = "colosse";

  return {
    famille,
    armement,
    graine,
    ampleur: Math.min(1, Math.max(0.42, Math.sqrt(Math.max(1, pv) / PV_DE_REFERENCE))),
    stigmate: signalement.inflige ?? null
  };
}
